import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, CheckCircle2, CreditCard } from 'lucide-react';
import { AuthContext } from '../App';
import { useToast } from '../hooks/use-toast';

const Settings = () => {
  const { user, login } = useContext(AuthContext);
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || '',
  });

  const save = () => {
    login({ ...user, ...form });
    toast({ title: 'Settings saved', description: 'Your profile has been updated.' });
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Configuration</div>
        <h1 className="font-display text-4xl text-stone-900">Settings</h1>
      </div>

      <div className="space-y-6">
        {/* Profile */}
        <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm p-8">
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Profile</div>
          <h2 className="font-display text-2xl text-stone-900 mb-6">Account Information</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-700">Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-700">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-stone-700">Company / CRO</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11" />
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={save} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">Save Changes</Button>
          </div>
        </div>

        {/* Plan */}
        <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm p-8 relative overflow-hidden">
          <div className="absolute inset-0 diagonal-stripes opacity-20 pointer-events-none"></div>
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Subscription</div>
            <h2 className="font-display text-2xl text-stone-900 mb-6">Plan & Billing</h2>

            <div className="border border-[#4b5320]/40 bg-[#4b5320]/5 rounded-sm p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Current Plan</div>
                  <div className="font-display text-2xl text-stone-900">Per-Client Subscription</div>
                  <div className="text-sm text-stone-700 mt-1">$6.99/month per active client · You only pay for clients with disputes</div>
                </div>
                <div className="text-right">
                  <Link to="/app/billing">
                    <Button variant="outline" className="mt-2 border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">
                      <CreditCard className="w-4 h-4 mr-2" /> Manage Billing
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-3">
              {[
                'Unlimited clients',
                'AI credit analysis',
                'Dispute letter generation',
                'CROA compliance tools',
                '3-bureau tracking',
                'Priority support',
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-stone-700">
                  <CheckCircle2 className="w-4 h-4 text-[#4b5320]" /> {f}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm p-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-4 h-4 text-[#4b5320]" />
            <div className="text-xs uppercase tracking-widest text-[#4b5320]">Compliance</div>
          </div>
          <h2 className="font-display text-2xl text-stone-900 mb-3">CROA Status</h2>
          <p className="text-sm text-stone-700 leading-relaxed">
            Your account operates under built-in CROA-compliant safeguards. Client consent, mandatory disclosures, and 3-day cancellation windows are enforced automatically.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-[#7fb685]/40 bg-[#7fb685]/10 text-[#7fb685] rounded-sm text-xs uppercase tracking-widest">
            <CheckCircle2 className="w-3.5 h-3.5" /> Fully Compliant
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
